import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { FC, useEffect, useState } from "react";
import { BsArchive } from "react-icons/bs";
import { HiEllipsisVertical } from "react-icons/hi2";
import { LuMerge } from "react-icons/lu";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import { toast } from "react-toastify";

import { Pages } from "../../../../app/constants/routes";
import { generateRoutePath } from "../../../../app/constants/routes/utils";
import { Badge } from "../../../../components/badge/Badge";
import Button from "../../../../components/button/Button";
import { LottieLoading } from "../../../../components/graphics/LottieLoading";
import OrganizationIconSvg from "../../../../components/svg/organizationIconSvg";
import { inactiveStatusId } from "../../../../constants/constants";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  useActivateOrganizationMutation,
  useAddPictureMutation,
  useArchiveOrganizationMutation,
  useDeleteContactMutation,
  useGetOrgContactQuery,
} from "../../../../slices/ContactsSlice";
import { CustomLink } from "../../../../support/CustomLink";
import { GetPageTitle } from "../../../../support/ScrollToTop";
import { primaryEmailOf, tabLabel } from "../../../../utils/tabCounts";
import { ContactDeleteConfirmModal } from "../../components/ContactDeleteConfirmModal";
import { ContactStatusConfirmModal } from "../../components/ContactStatusConfirmModal";
import { UpdateAvatarModal } from "../../modals/UpdateAvatarModal";
import { MergeContactModal } from "../../refactor/modals/MergeContactModal";
import { useClientTabCounts } from "../../useClientTabCounts";

export const OrganizationView: FC = () => {
  const navigate = useNavigate();
  const { organizationId } = useParams();

  const { data: organization, isLoading } = useGetOrgContactQuery({
    id: organizationId || "",
    orgId: getLinkedFirm()?.orgId || "",
  });
  const customerId = organization?.customerId || "";

  const [addPicture, {}] = useAddPictureMutation();
  const [deleteOrganization, { isLoading: isLoadingDelete }] =
    useDeleteContactMutation();
  const [activateOrganization, { isLoading: isLoadingActivate }] =
    useActivateOrganizationMutation();
  const [archiveOrganization, { isLoading: isLoadingArchive }] =
    useArchiveOrganizationMutation();

  // Each tab's count reads the same data as the tab (Roadmap 1842).
  const counts = useClientTabCounts(customerId, primaryEmailOf(organization));

  const [isOpenMerge, setIsOpenMerge] = useState(false);
  const [isOpenActivation, setIsOpenActivation] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isUpdateAvatar, setIsUpdateAvatar] = useState(false);

  const { profile, works, files, time, notes, billing, emails, portal } =
    Pages.organization.children;

  const ProfileComponent = profile.component;
  const WorksComponent = works.component;
  const FilesComponent = files.component;
  const TimeComponent = time.component;
  const NotesComponent = notes.component;
  const BillingComponent = billing.component;
  const EmailsComponent = emails.component;
  const PortalComponent = portal.component;

  const isActive = organization?.contactStatusEnumId !== inactiveStatusId;

  useEffect(() => {
    document.title = GetPageTitle(Pages.organization.label);
  }, []);

  if (isLoading || isLoadingActivate || isLoadingArchive) {
    return <LottieLoading />;
  }

  if (!organization) {
    return null;
  }

  return (
    <>
      <div className={"grid grid-cols-1 justify-center"}>
        <div className={"w-full max-w-[1920px]"}>
          <div className={"flex w-full items-center gap-4"}>
            <div
              className={
                "flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-full bg-[#581C87]"
              }>
              {organization.imageURL ? (
                <img src={organization.imageURL} alt={"avatar"} />
              ) : (
                <OrganizationIconSvg />
              )}
            </div>
            <div className={"flex min-w-0 flex-1 items-end"}>
              {/* Long names: one line, cut with "…" + full name on hover
                  (QA batch 8; it wrapped mid-word). */}
              <h1 className={"flex min-w-0 items-center gap-1 text-2xl"}>
                <span className={"truncate"} title={organization?.companyName}>
                  {organization?.companyName}
                </span>
                {organization?.isSyncedWithQBO && (
                  <img
                    src={
                      "https://cdn.shopify.com/app-store/listing_images/a9755168ba32bb3e7fa95c947d11069e/icon/CPafhOnvuf0CEAE=.png"
                    }
                    height={"24px"}
                    width={"24px"}
                  />
                )}
              </h1>
              {!isActive && (
                <Badge
                  label={"Archived"}
                  variant={"archived"}
                  className={"ml-2 shrink-0"}
                />
              )}
            </div>
          </div>
          <div className={"my-4 flex w-full justify-between"}>
            <div className={"tabs"}>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.organization.path,
                    organizationId || "",
                    profile.path,
                  ]),
                }}>
                {profile.label}
              </CustomLink>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.organization.path,
                    organizationId || "",
                    works.path,
                  ]),
                }}>
                {tabLabel(works.label, counts.works)}
              </CustomLink>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.organization.path,
                    organizationId || "",
                    files.path,
                  ]),
                }}>
                {tabLabel(files.label, counts.files)}
              </CustomLink>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.organization.path,
                    organizationId || "",
                    time.path,
                  ]),
                }}>
                {tabLabel(time.label, counts.time)}
              </CustomLink>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.organization.path,
                    organizationId || "",
                    notes.path,
                  ]),
                }}>
                {tabLabel(notes.label, counts.notes)}
              </CustomLink>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.organization.path,
                    organizationId || "",
                    billing.path,
                  ]),
                }}>
                {billing.label}
              </CustomLink>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.organization.path,
                    organizationId || "",
                    emails.path,
                  ]),
                }}>
                {tabLabel(emails.label, counts.emails)}
              </CustomLink>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.organization.path,
                    organizationId || "",
                    portal.path,
                  ]),
                }}>
                {portal.label}
              </CustomLink>
            </div>
            <div>
              <div className={"dropdown dropdown-end"}>
                <div>
                  <Button
                    label={""}
                    colorType={"ghost"}
                    size={"custom"}
                    icon={<HiEllipsisVertical size={24} />}
                    extraClasses={"min-h-fit h-fit w-fit p-1"}
                  />
                </div>
                <div
                  className={classNames(
                    "dropdown-content rounded-[5px] bg-white shadow",
                    isActive ? "w-[120px]" : "w-[180px]",
                  )}>
                  <div
                    onMouseDown={() => setIsOpenActivation(true)}
                    className={
                      "flex cursor-pointer items-center gap-2 rounded-[5px] px-[15px] py-[5px] hover:bg-gray-300"
                    }>
                    <BsArchive size={18} className={"text-purple"} />
                    {isActive ? "Archive" : "Move to active"}
                  </div>
                  {isActive && (
                    <div
                      onMouseDown={() => setIsOpenMerge(true)}
                      className={
                        "flex cursor-pointer items-center gap-2 rounded-[5px] px-[15px] py-[5px] hover:bg-gray-300"
                      }>
                      <LuMerge size={18} className={"text-purple"} />
                      Merge
                    </div>
                  )}
                  {!isActive && (
                    <div
                      onMouseDown={() => setIsOpenDelete(true)}
                      className={
                        "flex cursor-pointer items-center gap-2 rounded-[5px] px-[15px] py-[5px] hover:bg-gray-300"
                      }>
                      <FontAwesomeIcon
                        icon={faTrashCan}
                        className={"text-purple w-[18px]"}
                      />
                      Delete
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Routes>
            <Route
              index
              element={
                <Navigate
                  to={{
                    pathname: profile.path,
                  }}
                  replace={true}
                />
              }
            />
            <Route
              path={profile.path}
              element={<ProfileComponent organization={organization} />}
            />
            <Route
              path={works.path}
              element={<WorksComponent organization={organization} />}
            />
            <Route
              path={files.path}
              element={<FilesComponent customerId={customerId} />}
            />
            <Route
              path={time.path}
              element={<TimeComponent organization={organization} />}
            />
            <Route
              path={notes.path}
              element={<NotesComponent organization={organization} />}
            />
            <Route
              path={billing.path}
              element={
                <BillingComponent
                  customerId={organization?.customerId || ""}
                  organization={organization}
                />
              }
            />
            <Route
              path={emails.path + "/*"}
              element={<EmailsComponent organization={organization} />}
            />
            <Route
              path={portal.path}
              element={
                <PortalComponent
                  customerId={customerId}
                  organizationId={organization?.id}
                />
              }
            />
          </Routes>
          <div>
            <Outlet />
          </div>
        </div>
      </div>
      {isOpenActivation && (
        <ContactStatusConfirmModal
          onClose={() => setIsOpenActivation(false)}
          onConfirm={() => {
            isActive
              ? archiveOrganization({ id: organization?.id || "" })
              : activateOrganization({ id: organization?.id || "" });
            setIsOpenActivation(false);
          }}
          contactName={organization.companyName}
          isActive={isActive}
          isLoading={isLoadingActivate || isLoadingArchive}
        />
      )}
      {isUpdateAvatar && (
        <UpdateAvatarModal
          organization={organization}
          isActive={isUpdateAvatar}
          onClose={() => setIsUpdateAvatar(false)}
        />
      )}
      {isOpenMerge && organization && (
        <MergeContactModal
          customerId={organizationId || ""}
          isActive={isOpenMerge}
          onClose={() => setIsOpenMerge(false)}
          name={organization?.companyName || ""}
          type={"organization"}
        />
      )}
      {isOpenDelete && (
        <ContactDeleteConfirmModal
          contactName={organization.companyName}
          isLoading={isLoadingDelete}
          onClose={() => setIsOpenDelete(false)}
          onConfirm={() => {
            if (!organization.customerId) {
              return;
            }

            // Leave the page first (QA batch 8): otherwise the delete makes
            // this page load the deleted client again (500 / 404 errors).
            const deletedName = organization.companyName;
            navigate("/contacts");
            deleteOrganization({
              body: [organization.customerId],
              orgId: getLinkedFirm().orgId,
            })
              .unwrap()
              .catch(() =>
                toast(`Couldn't delete ${deletedName}. Please try again.`, {
                  type: "error",
                  pauseOnHover: false,
                }),
              );
          }}
        />
      )}
    </>
  );
};
